import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Trophy, ChevronLeft, ChevronRight, Play, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────────────────────────────────────────
   Helper: extract youtube video-id from any common URL format
───────────────────────────────────────────────────────────── */
function extractYoutubeId(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:v\/|u\/\w\/|embed\/|watch\?v=|shorts\/))([^#&?]{11})/
  );
  return match ? match[1] : null;
}

/* ─────────────────────────────────────────────────────────────
   Build the canonical multimedia array from sorteo data.
   Priority: sorteo.promocion.multimedia → sorteo.imagenes
───────────────────────────────────────────────────────────── */
function buildMultimedia(sorteo) {
  // 1. Use the structured promotional multimedia if available
  if (sorteo?.promocion?.multimedia?.length > 0) {
    return sorteo.promocion.multimedia;
  }

  // 2. Fallback to legacy imagenes array
  if (sorteo?.imagenes?.length > 0) {
    return sorteo.imagenes.map((url, i) => ({
      id: `img-${i}`,
      tipo: 'image',
      url,
      platform: url.startsWith('http') ? 'remote' : 'local',
    }));
  }

  return [];
}

/* ─────────────────────────────────────────────────────────────
   Resolve the final displayable URL for an item
───────────────────────────────────────────────────────────── */
function resolveUrl(item) {
  if (!item?.url) return '';
  return item.url.startsWith('http') ? item.url : `${BACKEND_URL}${item.url}`;
}

/* ─────────────────────────────────────────────────────────────
   Render a single media item (image, local video, or YouTube)
   Uses the same pattern as CampaignSection for compatibility.
───────────────────────────────────────────────────────────── */
const MediaItem = ({ item, isActive }) => {
  const isYoutube = item.platform === 'youtube' || !!extractYoutubeId(item.url);
  const url = resolveUrl(item);

  if (isYoutube) {
    // Extract video ID — support watch?v=, youtu.be/, embed/, shorts/
    const videoId =
      extractYoutubeId(item.url) ||
      item.url.split('v=')[1]?.split('&')[0] ||
      item.url.split('/').pop();

    if (!videoId) {
      return <div className="w-full h-full bg-black flex items-center justify-center"><span className="text-white/40 text-sm">Video no disponible</span></div>;
    }

    return (
      <div className="w-full h-full relative bg-black">
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=${isActive ? 1 : 0}&mute=1&controls=1&rel=0&modestbranding=1&iv_load_policy=3&enablejsapi=1`}
          title="Video del sorteo"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (item.tipo === 'image') {
    return (
      <img
        src={url}
        alt="Sorteo"
        className="w-full h-full object-cover"
      />
    );
  }

  // Local video
  return (
    <video
      src={url}
      className="w-full h-full object-cover"
      autoPlay={isActive}
      muted
      loop={isActive}
      playsInline
    />
  );
};

/* ─────────────────────────────────────────────────────────────
   3-D Coverflow Carousel (2+ items)
───────────────────────────────────────────────────────────── */
const CoverflowCarousel = ({ items, currentIndex, onIndexChange }) => {
  const total = items.length;

  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {/* 3D Stage */}
      <div
        className="relative w-full h-full"
        style={{ perspective: '1200px', perspectiveOrigin: '50% 50%' }}
      >
        {items.map((item, idx) => {
          let offset = idx - currentIndex;
          // Wrap around for circular carousel
          if (offset < -Math.floor(total / 2)) offset += total;
          if (offset > Math.floor(total / 2)) offset -= total;

          const abs = Math.abs(offset);
          const isActive = offset === 0;
          const isYoutube = item.platform === 'youtube' || !!extractYoutubeId(item.url);

          // 3D transform values
          const xPx = offset * 60 + '%';
          const zPx = isActive ? 0 : -abs * 200;
          const rotY = offset * -22;
          const scale = isActive ? 1 : Math.max(0.6, 0.82 - abs * 0.12);
          const opacity = abs > 2 ? 0 : 1 - abs * 0.35;

          // For non-active YouTube items: show thumbnail instead of iframe
          // (iframes in 3D-transformed/scaled contexts can render blank)
          const ytId = isYoutube
            ? extractYoutubeId(item.url) ||
              item.url.split('v=')[1]?.split('&')[0] ||
              item.url.split('/').pop()
            : null;
          const ytThumb = ytId
            ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`
            : null;

          return (
            <div
              key={item.id || idx}
              onClick={() => !isActive && onIndexChange(idx)}
              className={cn(
                'absolute inset-0 transition-all duration-700 ease-[cubic-bezier(.2,.8,.2,1)]',
                isActive ? 'cursor-default z-20' : 'cursor-pointer z-10'
              )}
              style={{
                transform: `translateX(${xPx}) translateZ(${zPx}px) rotateY(${rotY}deg) scale(${scale})`,
                opacity,
                filter: isActive ? 'none' : `brightness(${0.5 - abs * 0.15}) blur(${abs * 1}px)`,
              }}
            >
              {/* Card shell: relative so children can use absolute positioning */}
              <div
                className={cn(
                  'absolute inset-0 rounded-2xl overflow-hidden border transition-all duration-700 bg-black',
                  isActive
                    ? 'border-white/20 shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-white/10'
                    : 'border-white/5 shadow-2xl'
                )}
              >
                {/* ── MEDIA CONTENT ─────────────────────────── */}
                <div className="absolute inset-0 z-0">
                  <MediaItem item={item} isActive={isActive} />
                </div>

                {/* For non-active YouTube slots: show thumbnail overlay */}
                {isYoutube && !isActive && ytThumb && (
                  <div className="absolute inset-0 z-[1]">
                    <img
                      src={ytThumb}
                      alt="Video preview"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {/* Glass highlight */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/30 z-10 pointer-events-none" />

                {/* Video play badge (non-active video items) */}
                {item.tipo === 'video' && !isActive && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
                      {isYoutube ? (
                        <Youtube className="w-7 h-7 text-red-500" />
                      ) : (
                        <Play className="w-7 h-7 text-white ml-1" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MAIN: SorteoHero
   Props: sorteo (full object)
───────────────────────────────────────────────────────────── */
const SorteoHero = ({ sorteo }) => {
  const multimedia = buildMultimedia(sorteo);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef(null);
  const hasMultiple = multimedia.length > 1;

  /* Scroll helpers */
  const goTo = useCallback(
    (idx) => {
      if (isTransitioning || multimedia.length === 0) return;
      setIsTransitioning(true);
      setCurrentIndex((idx + multimedia.length) % multimedia.length);
      setTimeout(() => setIsTransitioning(false), 700);
    },
    [isTransitioning, multimedia.length]
  );

  const prev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);
  const next = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

  /* Autoplay: only for images, 7 s */
  useEffect(() => {
    if (!hasMultiple) return;
    const current = multimedia[currentIndex];
    if (current?.tipo === 'image' || current?.tipo === undefined) {
      autoplayRef.current = setInterval(next, 7000);
    }
    return () => clearInterval(autoplayRef.current);
  }, [currentIndex, hasMultiple, next, multimedia]);

  /* ── Background blur layer (ambient) ── */
  const bgItem = multimedia[currentIndex];
  const bgUrl = bgItem ? resolveUrl(bgItem) : '';
  const bgIsYoutube =
    bgItem?.platform === 'youtube' || (bgItem && extractYoutubeId(bgItem.url));

  /* ── Fallback: no media at all ── */
  if (multimedia.length === 0) {
    return (
      <div
        className="relative w-full h-72 md:h-96 flex items-center justify-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${sorteo.color_primario || '#f15a24'}, ${sorteo.color_secundario || '#f7931e'})` }}
      >
        <Trophy className="w-32 h-32 text-white/20" />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <HeroOverlayText sorteo={sorteo} />
      </div>
    );
  }

  /* ── Single media ── */
  if (multimedia.length === 1) {
    return (
      <div className="relative w-full h-72 md:h-[480px] overflow-hidden rounded-b-3xl bg-black">
        {/* Ambient blur layer */}
        <div className="absolute inset-0 scale-110 blur-3xl opacity-60 pointer-events-none overflow-hidden">
          {bgIsYoutube ? (
            <div className="w-full h-full bg-slate-900" />
          ) : bgItem?.tipo === 'image' ? (
            <img src={bgUrl} alt="" className="w-full h-full object-cover saturate-200" />
          ) : null}
        </div>

        {/* Media content */}
        <div className="absolute inset-0 z-0">
          <MediaItem item={multimedia[0]} isActive={true} />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40 pointer-events-none z-10" />

        <div className="absolute bottom-0 left-0 right-0 z-20">
          <HeroOverlayText sorteo={sorteo} />
        </div>
      </div>
    );
  }

  /* ── Multiple media: coverflow carousel ── */
  return (
    <div className="relative w-full overflow-hidden bg-slate-950"
      style={{ height: 'clamp(320px, 55vw, 560px)' }}>
      {/* ── AMBIENT BACKGROUND ── */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {bgIsYoutube ? (
          <div className="w-full h-full bg-slate-900" />
        ) : bgItem?.tipo === 'image' ? (
          <img
            key={`bg-${currentIndex}`}
            src={bgUrl}
            alt=""
            className="w-full h-full object-cover scale-110 blur-[50px] saturate-200 opacity-40 transition-all duration-1000"
          />
        ) : null}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60" />
        {/* Gradient vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.7)_100%)]" />
      </div>

      {/* ── CAROUSEL LAYER ── */}
      <div className="absolute inset-0 z-10">
        <CoverflowCarousel
          items={multimedia}
          currentIndex={currentIndex}
          onIndexChange={goTo}
        />
      </div>

      {/* ── GRADIENT OVERLAYS ── */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-1/4 bg-gradient-to-b from-black/40 to-transparent" />
      </div>

      {/* ── NAV ARROWS ── */}
      {hasMultiple && (
        <>
          <button
            onClick={prev}
            aria-label="Anterior"
            className={cn(
              'absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30',
              'w-10 h-10 md:w-12 md:h-12 rounded-full',
              'bg-black/40 backdrop-blur-md border border-white/10',
              'flex items-center justify-center text-white',
              'hover:bg-white/20 hover:border-white/30 hover:scale-110',
              'transition-all duration-300 shadow-lg'
            )}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Siguiente"
            className={cn(
              'absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30',
              'w-10 h-10 md:w-12 md:h-12 rounded-full',
              'bg-black/40 backdrop-blur-md border border-white/10',
              'flex items-center justify-center text-white',
              'hover:bg-white/20 hover:border-white/30 hover:scale-110',
              'transition-all duration-300 shadow-lg'
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* ── DOT INDICATORS ── */}
      {hasMultiple && (
        <div className="absolute bottom-[7rem] md:bottom-[6.5rem] left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {multimedia.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Ir a medio ${i + 1}`}
              className={cn(
                'rounded-full transition-all duration-300',
                i === currentIndex
                  ? 'w-6 h-2 bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.8)]'
                  : 'w-2 h-2 bg-white/30 hover:bg-white/60'
              )}
            />
          ))}
        </div>
      )}

      {/* ── OVERLAY TEXT ── */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        <HeroOverlayText sorteo={sorteo} />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Hero overlay text (title, description, badge)
───────────────────────────────────────────────────────────── */
const HeroOverlayText = ({ sorteo }) => (
  <div className="p-6 md:p-10 pb-8">
    <div className="max-w-6xl mx-auto">
      <Badge
        className="mb-3 text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1 rounded-lg border-none shadow-lg"
        style={{ backgroundColor: sorteo.color_secundario || '#f7931e' }}
      >
        {sorteo.tipo === 'etapas' ? '⚡ Sorteo por Etapas' : '🏆 Sorteo Único'}
      </Badge>
      <h1
        className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-2
                   [text-shadow:0_2px_20px_rgba(0,0,0,0.6)]"
        data-testid="sorteo-titulo"
        style={{ fontStyle: 'italic' }}
      >
        {sorteo.titulo}
      </h1>
      {sorteo.descripcion && (
        <p className="text-base md:text-lg text-white/80 font-medium max-w-2xl leading-relaxed
                       [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]">
          {sorteo.descripcion}
        </p>
      )}
    </div>
  </div>
);

export default SorteoHero;
